import {
  addDoc,
  collection,
  getDocs,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  deleteDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase/config";
import AIService from "./AIService";
import type { App } from "../types/app";
import { buildAppContextLines } from "../utils/appContextPrompt";
import {
  AdCopySuggestion,
  BudgetChannelMixPlan,
  CoreTargetAudienceProfile,
  CreateMarketingResearchDTO,
  MarketingActionChecklistItem,
  MarketingAsset,
  MarketingCreator,
  MarketingAdRecommendation,
  MarketingResearchReport,
  PlatformGrowthPlan,
  SocialPlatform,
  VideoPromptVariation,
} from "../types/marketing";

const MARKETING_REPORTS_COLLECTION = "appMarketingResearch";
const MARKETING_ASSETS_COLLECTION = "appMarketingAssets";
const MARKETING_CREATORS_COLLECTION = "appMarketingCreators";

const MARKETING_CORE_SCHEMA = {
  type: "object",
  properties: {
    summary: { type: "string" },
    recommendedPlatforms: {
      type: "array",
      items: {
        type: "string",
        enum: [
          "facebook",
          "instagram",
          "tiktok",
          "twitter",
          "reddit",
          "youtube",
          "linkedin",
        ],
      },
    },
    goToMarketStrategy: { type: "array", items: { type: "string" } },
    marketingInsights: { type: "array", items: { type: "string" } },
  },
  required: [
    "summary",
    "recommendedPlatforms",
    "goToMarketStrategy",
    "marketingInsights",
  ],
};

type MarketingAIResponse = {
  summary?: string;
  coreTargetAudience?: CoreTargetAudienceProfile;
  budgetChannelMixPlan?: BudgetChannelMixPlan;
  recommendedPlatforms?: SocialPlatform[];
  goToMarketStrategy?: string[];
  marketingInsights?: string[];
  platformPlans?: PlatformGrowthPlan[];
  adRecommendations?: Array<
    Omit<MarketingAdRecommendation, "recommendationId"> & {
      recommendationId?: string;
    }
  >;
};

export class MarketingService {
  static async conductMarketingResearch(
    userId: string,
    app: App,
    options?: {
      targetAudience?: string;
      goals?: string[];
      budgetRange?: string;
      model?: string;
    },
  ): Promise<MarketingResearchReport> {
    const dto: CreateMarketingResearchDTO = {
      appId: app.appId,
      appName: app.name,
      appDescription: app.description || "",
      appCategory: app.category || "General",
      appPlatform: app.platform,
      storeUrl: app.storeUrl,
      androidStoreUrl: app.androidStoreUrl,
      iosStoreUrl: app.iosStoreUrl,
      researchContext: app.researchContext,
      targetAudience: options?.targetAudience,
      goals: options?.goals,
      budgetRange: options?.budgetRange,
    };

    const candidatePlatforms = this.determineCandidatePlatforms(app);
    const prompt = this.buildMarketingPrompt(dto, candidatePlatforms);
    const ai = await this.generateMarketingResponse(
      prompt,
      options?.model || undefined,
    );

    const normalizedRecommendedPlatforms = this.normalizePlatforms(
      ai?.recommendedPlatforms,
      candidatePlatforms,
    );
    const normalizedPlans = this.normalizePlatformPlans(ai?.platformPlans);
    const fallbackPlans = this.buildFallbackPlatformPlans(
      normalizedRecommendedPlatforms,
      app,
    );
    const mergedPlans = this.mergePlatformPlans(
      normalizedPlans.length > 0 ? normalizedPlans : fallbackPlans,
      normalizedRecommendedPlatforms,
      app,
    );
    const finalPlans = this.applyPlatformSpecificity(mergedPlans, app);

    const normalizedRecommendations = this.normalizeAdRecommendations(
      ai?.adRecommendations,
    );
    const finalRecommendations = this.ensureRecommendationCoverage(
      normalizedRecommendations,
      finalPlans,
      normalizedRecommendedPlatforms,
      app,
    );

    const goToMarket = this.normalizeStringArray(ai?.goToMarketStrategy, 10);
    const marketingInsights = this.normalizeStringArray(
      ai?.marketingInsights,
      20,
    );
    const coreAudience = this.normalizeCoreTargetAudience(
      ai?.coreTargetAudience,
      app,
    );
    const budgetChannelMixPlan = this.normalizeBudgetChannelMixPlan(
      ai?.budgetChannelMixPlan,
      normalizedRecommendedPlatforms,
      options?.budgetRange,
    );
    const resolvedGoToMarket =
      goToMarket.length > 0 ? goToMarket : this.buildFallbackGoToMarket(app);
    const resolvedInsights =
      marketingInsights.length > 0
        ? marketingInsights
        : this.buildFallbackInsights(app);

    const report: MarketingResearchReport = {
      reportId: this.generateId("mkt_report"),
      userId,
      appId: app.appId,
      appName: app.name,
      appCategory: app.category || "General",
      model: options?.model || AIService.getDefaultModel(),
      summary:
        String(ai?.summary || "").trim() || "Marketing research generated.",
      coreTargetAudience: coreAudience,
      budgetChannelMixPlan,
      goToMarketStrategy: resolvedGoToMarket,
      marketingInsights: resolvedInsights,
      platformPlans: finalPlans,
      adRecommendations: finalRecommendations,
      actionChecklist: this.buildActionChecklist(
        resolvedGoToMarket,
        resolvedInsights,
        finalPlans,
      ),
      requestedBudgetRange: options?.budgetRange || undefined,
      createdAt: new Date(),
    };

    return report;
  }

  private static async generateMarketingResponse(
    prompt: string,
    model?: string,
  ): Promise<MarketingAIResponse> {
    try {
      const core = await AIService.generateJSON<MarketingAIResponse>(prompt, {
        model,
        useGoogleSearch: true,
        structuredOutputSchema: MARKETING_CORE_SCHEMA,
        structuredOutputName: "app_marketing_core",
        temperature: 0.2,
        maxTokens: 4096,
      });

      const detailsPrompt = [
        "Using the research context below, return valid JSON only (no markdown).",
        "Generate these keys only:",
        "- coreTargetAudience",
        "- budgetChannelMixPlan",
        "- platformPlans",
        "- adRecommendations",
        "",
        "Rules:",
        "- For image adRecommendations include exactly 4 adCopyVariations. For video adRecommendations include 4 videoPromptVariations with recommendedLengthSeconds per provider.",
        "- Include platformTargetUsers with detailed audience traits per platform.",
        "- Ensure budgetChannelMixPlan has platform-specific splitPercent, testBudget, and scaleCriteria entries.",
        "- Ensure spendSplit totals approximately 100 across recommended platforms.",
        "- Keep output specific, concise, and execution-ready.",
        "",
        "Context:",
        prompt,
        "",
        `Core result JSON: ${JSON.stringify(core)}`,
      ].join("\n");

      const detailsResponse = await AIService.generateText(detailsPrompt, {
        model,
        useGoogleSearch: false,
        temperature: 0.2,
        maxTokens: 4096,
      });

      const details =
        this.parseJsonResponse<Partial<MarketingAIResponse>>(
          detailsResponse.text,
        ) || {};

      return {
        ...core,
        ...details,
      };
    } catch (error: any) {
      const message = String(error?.message || "");
      const isSchemaDepthError =
        message.includes("maximum allowed nesting depth") ||
        message.includes("GenerationConfig") ||
        message.includes("INVALID_ARGUMENT");

      if (!isSchemaDepthError) {
        throw error;
      }

      const fallbackPrompt = [
        prompt,
        "",
        "Return valid JSON only. No markdown, no commentary.",
        "Required keys: summary, coreTargetAudience, budgetChannelMixPlan, recommendedPlatforms, goToMarketStrategy, marketingInsights, platformPlans, adRecommendations.",
      ].join("\n");

      const fallback = await AIService.generateText(fallbackPrompt, {
        model,
        useGoogleSearch: true,
        temperature: 0.2,
        maxTokens: 4096,
      });

      const parsed = this.parseJsonResponse<MarketingAIResponse>(fallback.text);
      if (!parsed) {
        throw new Error(
          "Failed to parse marketing response JSON from fallback generation.",
        );
      }

      return parsed;
    }
  }

  static async saveMarketingReport(
    report: MarketingResearchReport,
  ): Promise<string> {
    const q = query(
      collection(db, MARKETING_REPORTS_COLLECTION),
      where("userId", "==", report.userId),
      where("reportId", "==", report.reportId),
    );
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      await updateDoc(snapshot.docs[0].ref, {
        ...report,
        savedAt: serverTimestamp(),
      });
      return snapshot.docs[0].id;
    }

    const docRef = await addDoc(collection(db, MARKETING_REPORTS_COLLECTION), {
      ...report,
      createdAt: serverTimestamp(),
      savedAt: serverTimestamp(),
    });
    return docRef.id;
  }

  static async updateMarketingReport(
    userId: string,
    reportId: string,
    updates: Partial<MarketingResearchReport>,
  ): Promise<void> {
    const q = query(
      collection(db, MARKETING_REPORTS_COLLECTION),
      where("userId", "==", userId),
      where("reportId", "==", reportId),
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      throw new Error("Report not found");
    }
    await updateDoc(snapshot.docs[0].ref, {
      ...updates,
      savedAt: serverTimestamp(),
    });
  }

  static async deleteMarketingReport(
    userId: string,
    reportId: string,
  ): Promise<void> {
    const q = query(
      collection(db, MARKETING_REPORTS_COLLECTION),
      where("userId", "==", userId),
      where("reportId", "==", reportId),
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      throw new Error("Report not found");
    }
    await deleteDoc(snapshot.docs[0].ref);
  }

  static async updateActionChecklist(
    userId: string,
    reportId: string,
    checklist: MarketingActionChecklistItem[],
  ): Promise<void> {
    await this.updateMarketingReport(userId, reportId, {
      actionChecklist: checklist,
    });
  }

  static async getUserMarketingReports(
    userId: string,
  ): Promise<MarketingResearchReport[]> {
    const q = query(
      collection(db, MARKETING_REPORTS_COLLECTION),
      where("userId", "==", userId),
    );
    const snapshot = await getDocs(q);
    return this.dedupeReports(
      snapshot.docs.map((item) => this.mapReport(item.data())),
    );
  }

  static async getAppMarketingReports(
    userId: string,
    appId: string,
  ): Promise<MarketingResearchReport[]> {
    const q = query(
      collection(db, MARKETING_REPORTS_COLLECTION),
      where("userId", "==", userId),
      where("appId", "==", appId),
    );
    const snapshot = await getDocs(q);
    return this.dedupeReports(
      snapshot.docs.map((item) => this.mapReport(item.data())),
    );
  }

  static async createAdCreativeFromRecommendation(input: {
    userId: string;
    app: App;
    reportId: string;
    recommendation: MarketingAdRecommendation;
    provider?: "gemini" | "openai";
    model?: string;
    adCopyOverride?: AdCopySuggestion;
    videoPromptOverride?: VideoPromptVariation;
    videoSettings?: {
      seconds?: number;
      aspectRatio?: "16:9" | "9:16";
      size?: "1280x720" | "720x1280" | "1024x1792" | "1792x1024";
    };
  }): Promise<MarketingAsset[]> {
    const recommendation = input.recommendation;
    const selectedCopy =
      input.adCopyOverride ||
      recommendation.adCopyVariations?.[0] ||
      this.buildDefaultAdCopyVariations(
        recommendation.platform,
        input.app.name,
      )[0];
    const selectedVideoPrompt =
      input.videoPromptOverride ||
      recommendation.videoPromptVariations?.[0] ||
      this.buildDefaultVideoPromptVariations(
        recommendation.platform,
        input.app.name,
      )[0];
    const prompt = [
      `Create a high-performing social ad creative ${
        recommendation.assetType === "video" ? "storyboard keyframe" : "image"
      } for ${recommendation.platform}.`,
      ...buildAppContextLines(input.app),
      `Audience: ${recommendation.audience}`,
      `Platform target users: ${
        recommendation.platformTargetUsers.join(", ") || "N/A"
      }`,
      `Angle: ${recommendation.angle}`,
      `Creative prompt guidance: ${recommendation.imagePrompt}`,
      ...(recommendation.assetType === "video"
        ? [
            `Video variation: ${selectedVideoPrompt.title}`,
            `Detailed video generation prompt: ${selectedVideoPrompt.detailedPrompt}`,
            `Target video duration: ${this.resolveRecommendedVideoSeconds(
              selectedVideoPrompt,
              input.provider || "gemini",
            )} seconds`,
            `Style: polished vertical short-form ad, fast hook in first second, clear value demonstration, and strong CTA ending.`,
            "Quality constraints: avoid warped/deformed text, avoid misspellings, avoid broken or unreadable UI, avoid flicker/jitter between shots, avoid unnatural hand/finger/body motion, avoid impossible anatomy, keep scene continuity and realistic motion physics.",
            "UI constraints: mobile app screens must be coherent and functional-looking, with aligned elements, consistent iconography, and legible labels.",
            "Human realism constraints: natural facial expressions, realistic limbs/hands/fingers, no extra digits, no merged limbs, and no distorted body proportions.",
          ]
        : [
            `Ad headline context: ${selectedCopy.headline}`,
            `Ad primary text context: ${selectedCopy.primaryText}`,
            `Style: polished, mobile-first ad, clear focal point, no text overlays in image.`,
            "Quality constraints: avoid warped/deformed text, avoid misspellings, avoid malformed logos/icons, avoid distorted UI screens, avoid unnatural or deformed hands/fingers/faces/bodies, and avoid visual artifacts.",
            "UI constraints: app UI should look production-ready with clean hierarchy, aligned components, realistic spacing, and readable labels.",
            "Typography constraints: if any text appears in-scene, keep spelling correct and glyphs clean; no gibberish characters.",
            "Accessibility contrast constraints: keep foreground-to-background contrast strong and readable (target WCAG-like 4.5:1 minimum for text-like or UI-label elements).",
            "Framing constraints: keep all critical content fully in-frame, with safe margins on all sides; do not crop off text, logos, CTA elements, faces, hands, or device screens.",
            "Composition constraints: keep the focal subject centered or intentionally rule-of-thirds aligned; avoid edge-cutoff of important objects and avoid cramped layouts.",
            "Ad-safe-area constraints: preserve a clear interior safe area so that any platform UI overlays will not obscure key visual message.",
            "Repurpose-safe layout constraints: place all core messaging and primary visual elements inside the center 60% of the canvas (both horizontally and vertically).",
            "Keep outer edges as flexible background space; avoid placing key text or branding near corners/edges so crops to square, portrait, and landscape remain usable.",
            "If text is present, keep it short and centered, with generous padding around it for multi-platform cropping.",
          ]),
      recommendation.assetType === "video"
        ? "This should look like a compelling keyframe for a short-form video ad."
        : "This should look like a high-performing static ad image.",
    ].join("\n");

    const created: MarketingAsset[] = [];
    if (recommendation.assetType === "video") {
      const generated = await AIService.generateVideo(prompt, {
        appId: input.app.appId,
        assetCategory: `marketing/${recommendation.platform}`,
        provider: input.provider || "gemini",
        model: input.model || undefined,
        aspectRatio: input.videoSettings?.aspectRatio || "9:16",
        size: input.videoSettings?.size || "720x1280",
        seconds:
          input.videoSettings?.seconds ||
          this.resolveRecommendedVideoSeconds(
            selectedVideoPrompt,
            input.provider || "gemini",
          ),
      });

      for (const video of generated.videos || []) {
        const record: MarketingAsset = {
          assetRecordId: this.generateId("mkt_asset"),
          userId: input.userId,
          appId: input.app.appId,
          appName: input.app.name,
          reportId: input.reportId,
          recommendationId: recommendation.recommendationId,
          platform: recommendation.platform,
          mediaType: "video",
          prompt: selectedVideoPrompt.detailedPrompt,
          headline: selectedVideoPrompt.title,
          primaryText: "",
          callToAction: "",
          hashtags: [],
          video: {
            assetId: video.assetId,
            provider: video.provider,
            model: video.model,
            downloadUrl: video.downloadUrl,
            storagePath: video.storagePath,
            mimeType: video.mimeType,
            sizeBytes: video.sizeBytes,
            createdAt: video.createdAt,
          },
          createdAt: new Date(),
        };

        await addDoc(collection(db, MARKETING_ASSETS_COLLECTION), {
          ...record,
          type: "video",
          createdAt: serverTimestamp(),
        });
        created.push(record);
      }
      return created;
    }

    const generated = await AIService.generateImage(prompt, {
      appId: input.app.appId,
      assetCategory: `marketing/${recommendation.platform}`,
      provider: input.provider || undefined,
      model: input.model || undefined,
      aspectRatio: "1:1",
      n: 1,
    });

    for (const image of generated.images || []) {
      const record: MarketingAsset = {
        assetRecordId: this.generateId("mkt_asset"),
        userId: input.userId,
        appId: input.app.appId,
        appName: input.app.name,
        reportId: input.reportId,
        recommendationId: recommendation.recommendationId,
        platform: recommendation.platform,
        mediaType: "image",
        prompt: recommendation.imagePrompt,
        headline: selectedCopy.headline,
        primaryText: selectedCopy.primaryText,
        callToAction: selectedCopy.callToAction,
        hashtags: selectedCopy.hashtags,
        image: {
          assetId: image.assetId,
          provider: image.provider,
          model: image.model,
          downloadUrl: image.downloadUrl,
          storagePath: image.storagePath,
          mimeType: image.mimeType,
          sizeBytes: image.sizeBytes,
          createdAt: image.createdAt,
        },
        createdAt: new Date(),
      };

      await addDoc(collection(db, MARKETING_ASSETS_COLLECTION), {
        ...record,
        type: "image",
        createdAt: serverTimestamp(),
      });
      created.push(record);
    }

    return created;
  }

  static async getAppMarketingAssets(
    userId: string,
    appId: string,
  ): Promise<MarketingAsset[]> {
    const q = query(
      collection(db, MARKETING_ASSETS_COLLECTION),
      where("userId", "==", userId),
      where("appId", "==", appId),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((item) => this.mapAsset(item.data()));
  }

  static async deleteMarketingAsset(
    userId: string,
    asset: MarketingAsset,
  ): Promise<void> {
    await AIService.deleteGeneratedAsset({
      appId: asset.appId,
      storagePath: asset.image?.storagePath || asset.video?.storagePath,
      assetId: asset.image?.assetId || asset.video?.assetId,
      marketingAssetRecordId: asset.assetRecordId,
    });

    // Defensive cleanup in case backend already removed nothing due to stale records.
    const q = query(
      collection(db, MARKETING_ASSETS_COLLECTION),
      where("userId", "==", userId),
      where("assetRecordId", "==", asset.assetRecordId),
    );
    const snapshot = await getDocs(q);
    for (const doc of snapshot.docs) {
      await deleteDoc(doc.ref);
    }
  }

  static async createMarketingAssetsZip(input: {
    appId: string;
    reportId?: string;
  }): Promise<{
    ok: boolean;
    appId: string;
    totalAssets: number;
    storagePath: string;
    downloadUrl: string;
    fileName: string;
  }> {
    return await AIService.createMarketingAssetsZip({
      appId: input.appId,
      reportId: input.reportId,
    });
  }

  static async repurposeMarketingAssets(input: {
    appId: string;
    sourceStoragePath: string;
    targets: Array<{
      platform: string;
      formatKey: string;
      width: number;
      height: number;
    }>;
    crop?: {
      focalX?: number;
      focalY?: number;
      zoom?: number;
    };
  }): Promise<{
    ok: boolean;
    appId: string;
    sourceStoragePath: string;
    fileName: string;
    storagePath: string;
    downloadUrl: string;
    created: Array<{
      platform: string;
      formatKey: string;
      width: number;
      height: number;
      fileName: string;
    }>;
  }> {
    return await AIService.repurposeMarketingAssets(input);
  }

  static async addMarketingCreator(input: {
    userId: string;
    appId: string;
    reportId?: string;
    name: string;
    handle?: string;
    platform: string;
    audienceSize?: number;
    niche?: string;
    email?: string;
    ratePaid: number;
    currency?: string;
    status?: MarketingCreator["status"];
    notes?: string;
  }): Promise<string> {
    const creatorId = this.generateId("mkt_creator");
    const payload = {
      creatorId,
      userId: input.userId,
      appId: input.appId,
      reportId: input.reportId || null,
      name: String(input.name || "").trim(),
      handle: String(input.handle || "").trim() || null,
      platform: String(input.platform || "").trim() || "Unknown",
      audienceSize:
        Number.isFinite(Number(input.audienceSize)) &&
        Number(input.audienceSize) > 0
          ? Number(input.audienceSize)
          : null,
      niche: String(input.niche || "").trim() || null,
      email: String(input.email || "").trim() || null,
      ratePaid: Math.max(0, Number(input.ratePaid || 0)),
      currency: String(input.currency || "USD").trim() || "USD",
      status: input.status || "planned",
      notes: String(input.notes || "").trim() || null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const ref = await addDoc(
      collection(db, MARKETING_CREATORS_COLLECTION),
      payload,
    );
    return ref.id;
  }

  static async updateMarketingCreator(
    userId: string,
    creatorId: string,
    updates: Partial<MarketingCreator>,
  ): Promise<void> {
    const q = query(
      collection(db, MARKETING_CREATORS_COLLECTION),
      where("userId", "==", userId),
      where("creatorId", "==", creatorId),
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      throw new Error("Creator not found");
    }

    const sanitized = {
      ...(updates.name !== undefined
        ? { name: String(updates.name).trim() }
        : {}),
      ...(updates.handle !== undefined
        ? { handle: String(updates.handle || "").trim() || null }
        : {}),
      ...(updates.platform !== undefined
        ? { platform: String(updates.platform || "").trim() }
        : {}),
      ...(updates.audienceSize !== undefined
        ? {
            audienceSize:
              Number.isFinite(Number(updates.audienceSize)) &&
              Number(updates.audienceSize) > 0
                ? Number(updates.audienceSize)
                : null,
          }
        : {}),
      ...(updates.niche !== undefined
        ? { niche: String(updates.niche || "").trim() || null }
        : {}),
      ...(updates.email !== undefined
        ? { email: String(updates.email || "").trim() || null }
        : {}),
      ...(updates.ratePaid !== undefined
        ? { ratePaid: Math.max(0, Number(updates.ratePaid || 0)) }
        : {}),
      ...(updates.currency !== undefined
        ? { currency: String(updates.currency || "USD").trim() || "USD" }
        : {}),
      ...(updates.status !== undefined ? { status: updates.status } : {}),
      ...(updates.notes !== undefined
        ? { notes: String(updates.notes || "").trim() || null }
        : {}),
      updatedAt: serverTimestamp(),
    };

    await updateDoc(snapshot.docs[0].ref, sanitized);
  }

  static async deleteMarketingCreator(
    userId: string,
    creatorId: string,
  ): Promise<void> {
    const q = query(
      collection(db, MARKETING_CREATORS_COLLECTION),
      where("userId", "==", userId),
      where("creatorId", "==", creatorId),
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      throw new Error("Creator not found");
    }
    await deleteDoc(snapshot.docs[0].ref);
  }

  static async getAppMarketingCreators(
    userId: string,
    appId: string,
    reportId?: string,
  ): Promise<MarketingCreator[]> {
    const constraints = [
      where("userId", "==", userId),
      where("appId", "==", appId),
    ];
    if (reportId) {
      constraints.push(where("reportId", "==", reportId));
    }
    const q = query(
      collection(db, MARKETING_CREATORS_COLLECTION),
      ...constraints,
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((item) => this.mapCreator(item.data()));
  }

  private static buildMarketingPrompt(
    input: CreateMarketingResearchDTO,
    candidatePlatforms: SocialPlatform[] = ["instagram", "tiktok", "youtube"],
  ): string {
    return [
      "Conduct app marketing research and produce an execution-ready marketing strategy.",
      "Use current web information and search-grounded insights.",
      "",
      ...buildAppContextLines({
        name: input.appName,
        description: input.appDescription,
        category: input.appCategory,
        platform: input.appPlatform,
        storeUrl: input.storeUrl,
        androidStoreUrl: input.androidStoreUrl,
        iosStoreUrl: input.iosStoreUrl,
        researchContext: input.researchContext,
      }),
      `Target audience: ${input.targetAudience || "N/A"}`,
      `Growth goals: ${
        input.goals?.join(", ") || "Acquire users and improve retention"
      }`,
      `Marketing budget range: ${
        input.budgetRange || "N/A (AI should recommend practical range)"
      }`,
      `Candidate social platforms likely to perform well for this app: ${candidatePlatforms.join(
        ", ",
      )}`,
      "",
      "Requirements:",
      "- Include a clear go-to-market plan covering launch and post-launch phases.",
      "- Define a global coreTargetAudience profile with detailed demographics and interests.",
      "- Provide practical marketing insights and growth levers.",
      "- Select only platforms where this app is likely to perform well; exclude poor-fit platforms.",
      "- Return selected platforms in recommendedPlatforms.",
      "- For each selected platform, include growth strategy with paid targeting details (coreDemographics + interests).",
      "- For each selected platform, include platformTargetUsers as detailed audience traits for paid ads.",
      "- Provide ad recommendations for each selected platform with platform-native conversion-focused copy and prompt.",
      "- Set assetType per recommendation as image or video depending on platform and app fit.",
      "- For image recommendations, provide exactly 4 adCopyVariations with platform-appropriate CTA and conversion intent.",
      "- For video recommendations, include videoPromptVariations (4 items) each with: title, detailedPrompt, recommendedLengthSeconds { geminiVeo, openAISora }.",
      "- Video prompts must be detailed production prompts, not short headline/primary text copy.",
      "- Budget and channel mix must be tailored per platform with unique split/test/scale recommendations.",
      "- Spend split across selected platforms must total 100%.",
      "- Use the provided marketing budget range as a hard context when present.",
      "- Creative prompt must be detailed and specific to the platform ad format.",
      "- Keep recommendations specific to this app and category.",
      "- Use concise, implementation-focused outputs.",
    ].join("\n");
  }

  private static normalizeStringArray(value: unknown, max = 20): string[] {
    if (!Array.isArray(value)) return [];
    return value
      .map((item) => String(item || "").trim())
      .filter((item) => item.length > 0)
      .slice(0, max);
  }

  private static normalizePlatforms(
    value: unknown,
    fallback: SocialPlatform[],
  ): SocialPlatform[] {
    if (!Array.isArray(value)) return fallback;
    const platforms = value.filter((item) =>
      this.isValidPlatform(item),
    ) as SocialPlatform[];
    const unique = [...new Set(platforms)];
    return unique.length > 0 ? unique : fallback;
  }

  private static normalizeCoreTargetAudience(
    value: unknown,
    app: App,
  ): CoreTargetAudienceProfile {
    const raw = (
      value && typeof value === "object" ? value : {}
    ) as Partial<CoreTargetAudienceProfile>;
    const demographics = this.normalizeStringArray(raw.demographics, 12);
    const interests = this.normalizeStringArray(raw.interests, 12);
    const painPoints = this.normalizeStringArray(raw.painPoints, 8);
    const motivations = this.normalizeStringArray(raw.motivations, 8);

    return {
      summary:
        String(raw.summary || "").trim() ||
        `${app.name} is best positioned for mobile-first users seeking ${
          app.category || "app"
        } outcomes quickly.`,
      demographics:
        demographics.length > 0
          ? demographics
          : [
              "Ages 18-34",
              "Mobile-first users",
              `${app.category || "App"} category interest`,
            ],
      interests:
        interests.length > 0
          ? interests
          : [
              "Mobile apps",
              "Personal improvement",
              `${app.category || "Lifestyle"} content`,
            ],
      painPoints:
        painPoints.length > 0
          ? painPoints
          : [
              "Current alternatives are slow or manual",
              "Low consistency and habit adherence",
            ],
      motivations:
        motivations.length > 0
          ? motivations
          : [
              "Save time",
              "Get measurable outcomes",
              "Build sustainable routines",
            ],
    };
  }

  private static normalizeBudgetChannelMixPlan(
    value: unknown,
    platforms: SocialPlatform[],
    budgetRange?: string,
  ): BudgetChannelMixPlan {
    const fallback = this.buildFallbackBudgetChannelMixPlan(
      platforms,
      budgetRange,
    );
    if (!value || typeof value !== "object") return fallback;
    const raw = value as Partial<BudgetChannelMixPlan>;

    const recommendedMonthlyBudget =
      String(raw.recommendedMonthlyBudget || "").trim() ||
      fallback.recommendedMonthlyBudget;

    const spendSplit = Array.isArray(raw.spendSplit)
      ? raw.spendSplit
          .filter((item) => item && typeof item === "object")
          .map((item: any) => ({
            platform: this.isValidPlatform(item.platform)
              ? item.platform
              : null,
            splitPercent: Number(item.splitPercent || 0),
            testBudget: String(item.testBudget || "").trim(),
            scaleCriteria: String(item.scaleCriteria || "").trim(),
          }))
          .filter((item) => item.platform !== null)
          .map((item) => ({
            platform: item.platform as SocialPlatform,
            splitPercent: Math.max(0, Math.min(100, item.splitPercent || 0)),
            testBudget: item.testBudget || "",
            scaleCriteria: item.scaleCriteria || "",
          }))
      : [];

    if (spendSplit.length === 0) {
      return fallback;
    }

    const selected = platforms.length
      ? platforms
      : (spendSplit.map((item) => item.platform) as SocialPlatform[]);
    const selectedSet = new Set(selected);

    const filtered = spendSplit.filter((item) =>
      selectedSet.has(item.platform),
    );
    if (!filtered.length) {
      return fallback;
    }

    const aggregatedByPlatform = Array.from(
      filtered
        .reduce((map, item) => {
          const existing = map.get(item.platform);
          if (!existing) {
            map.set(item.platform, { ...item });
          } else {
            map.set(item.platform, {
              platform: item.platform,
              splitPercent:
                (existing.splitPercent || 0) + (item.splitPercent || 0),
              testBudget: existing.testBudget || item.testBudget,
              scaleCriteria: existing.scaleCriteria || item.scaleCriteria,
            });
          }
          return map;
        }, new Map<SocialPlatform, { platform: SocialPlatform; splitPercent: number; testBudget: string; scaleCriteria: string }>())
        .values(),
    );

    const totalPercent = aggregatedByPlatform.reduce(
      (sum, item) => sum + (item.splitPercent || 0),
      0,
    );
    const normalizedPercents =
      totalPercent > 0
        ? aggregatedByPlatform.map((item) => ({
            ...item,
            splitPercent: Math.max(
              1,
              Math.round((item.splitPercent / totalPercent) * 100),
            ),
          }))
        : fallback.spendSplit;

    const uniqueCriteriaCount = new Set(
      normalizedPercents.map((item) => String(item.scaleCriteria || "").trim()),
    ).size;
    const uniqueTestBudgetCount = new Set(
      normalizedPercents.map((item) => String(item.testBudget || "").trim()),
    ).size;

    return {
      recommendedMonthlyBudget,
      spendSplit: normalizedPercents.map((item) => {
        const details = this.buildPlatformBudgetDetail(
          item.platform,
          item.splitPercent,
          recommendedMonthlyBudget,
          budgetRange,
        );
        return {
          platform: item.platform,
          splitPercent: item.splitPercent,
          testBudget:
            uniqueTestBudgetCount <= 1
              ? details.testBudget
              : item.testBudget || details.testBudget,
          scaleCriteria:
            uniqueCriteriaCount <= 1
              ? details.scaleCriteria
              : item.scaleCriteria || details.scaleCriteria,
        };
      }),
    };
  }

  private static isValidPlatform(value: unknown): value is SocialPlatform {
    return (
      value === "facebook" ||
      value === "instagram" ||
      value === "tiktok" ||
      value === "twitter" ||
      value === "reddit" ||
      value === "youtube" ||
      value === "linkedin"
    );
  }

  private static normalizePlatformPlans(value: unknown): PlatformGrowthPlan[] {
    if (!Array.isArray(value)) return [];
    return value
      .filter((item) => item && typeof item === "object")
      .map((item: any) => ({
        platform: this.isValidPlatform(item.platform)
          ? item.platform
          : "instagram",
        whyItMatters: String(
          item.whyItMatters || "This platform can drive growth for this app.",
        ).trim(),
        organicStrategy: this.normalizeStringArray(item.organicStrategy, 8),
        paidStrategy: this.normalizeStringArray(item.paidStrategy, 8),
        paidTargeting: {
          coreDemographics: this.normalizeStringArray(
            item.paidTargeting?.coreDemographics,
            8,
          ),
          interests: this.normalizeStringArray(
            item.paidTargeting?.interests,
            8,
          ),
        },
        contentPillars: this.normalizeStringArray(item.contentPillars, 8),
        cadence: String(item.cadence || "3-5 posts per week").trim(),
        kpis: this.normalizeStringArray(item.kpis, 8),
      }));
  }

  private static normalizeAdRecommendations(
    value: unknown,
  ): MarketingAdRecommendation[] {
    if (!Array.isArray(value)) return [];
    return value
      .filter((item) => item && typeof item === "object")
      .map((item: any) => {
        const platform = this.isValidPlatform(item.platform)
          ? item.platform
          : "instagram";
        return {
          recommendationId: this.generateId("ad_rec"),
          platform,
          assetType: item.assetType === "video" ? "video" : "image",
          platformTargetUsers: this.normalizeStringArray(
            item.platformTargetUsers,
            8,
          ),
          angle: String(item.angle || "").trim(),
          audience: String(item.audience || "").trim(),
          imagePrompt: String(item.imagePrompt || "").trim(),
          adCopyVariations:
            item.assetType === "video"
              ? []
              : this.normalizeAdCopyVariations(item.adCopyVariations, platform),
          videoPromptVariations:
            item.assetType === "video"
              ? this.normalizeVideoPromptVariations(
                  item.videoPromptVariations,
                  platform,
                )
              : [],
        };
      });
  }

  private static normalizeAdCopyVariations(
    value: unknown,
    platform: SocialPlatform,
  ) {
    if (!Array.isArray(value))
      return this.buildDefaultAdCopyVariations(platform, "Your App");
    const normalized = value
      .filter((item) => item && typeof item === "object")
      .map((item: any) => ({
        headline: String(item.headline || "").trim(),
        primaryText: String(item.primaryText || "").trim(),
        callToAction: String(item.callToAction || "").trim(),
        hashtags: this.normalizeStringArray(item.hashtags, 8),
      }))
      .filter((item) => item.headline && item.primaryText && item.callToAction)
      .slice(0, 4);
    return normalized;
  }

  private static ensureRecommendationCoverage(
    recommendations: MarketingAdRecommendation[],
    plans: PlatformGrowthPlan[],
    selectedPlatforms: SocialPlatform[],
    app: App,
  ): MarketingAdRecommendation[] {
    const allowed = new Set(selectedPlatforms);
    const withCoverage = recommendations
      .filter((item) => allowed.has(item.platform))
      .map((item) => ({
        ...item,
        platformTargetUsers:
          item.platformTargetUsers.length > 0
            ? item.platformTargetUsers
            : this.defaultPlatformTargetUsers(item.platform, app),
        angle: item.angle || `Core value proposition for ${app.name}`,
        audience:
          item.audience || "Users likely to benefit from this app category",
        imagePrompt:
          item.imagePrompt ||
          this.defaultPromptForPlatform(
            item.platform,
            app.name,
            item.assetType,
          ),
        adCopyVariations:
          item.assetType === "video"
            ? []
            : item.adCopyVariations.length >= 4
            ? item.adCopyVariations.slice(0, 4)
            : this.buildDefaultAdCopyVariations(item.platform, app.name),
        videoPromptVariations:
          item.assetType === "video"
            ? item.videoPromptVariations &&
              item.videoPromptVariations.length > 0
              ? item.videoPromptVariations.slice(0, 4)
              : this.buildDefaultVideoPromptVariations(item.platform, app.name)
            : [],
      }));
    const existingPlatforms = new Set(
      withCoverage.map((item) => item.platform),
    );

    (plans || []).forEach((plan) => {
      if (!allowed.has(plan.platform)) return;
      if (existingPlatforms.has(plan.platform)) return;
      const assetType = this.defaultAssetTypeForPlatform(plan.platform);
      withCoverage.push({
        recommendationId: this.generateId("ad_rec"),
        platform: plan.platform,
        assetType,
        platformTargetUsers: this.defaultPlatformTargetUsers(
          plan.platform,
          app,
        ),
        angle: `Core value proposition for ${app.name} on ${plan.platform}`,
        audience: "Users likely to benefit from this app category",
        imagePrompt: this.defaultPromptForPlatform(
          plan.platform,
          app.name,
          assetType,
        ),
        adCopyVariations:
          assetType === "video"
            ? []
            : this.buildDefaultAdCopyVariations(plan.platform, app.name),
        videoPromptVariations:
          assetType === "video"
            ? this.buildDefaultVideoPromptVariations(plan.platform, app.name)
            : [],
      });
    });

    return withCoverage;
  }

  private static mergePlatformPlans(
    plans: PlatformGrowthPlan[],
    selectedPlatforms: SocialPlatform[],
    app: App,
  ): PlatformGrowthPlan[] {
    const allowed = new Set(selectedPlatforms);
    const initial = plans.filter((item) => allowed.has(item.platform));
    const dedupedMap = new Map<SocialPlatform, PlatformGrowthPlan>();
    initial.forEach((plan) => {
      const existing = dedupedMap.get(plan.platform);
      if (!existing) {
        dedupedMap.set(plan.platform, plan);
        return;
      }
      dedupedMap.set(plan.platform, {
        platform: plan.platform,
        whyItMatters: existing.whyItMatters || plan.whyItMatters,
        organicStrategy: this.mergeUnique(
          existing.organicStrategy,
          plan.organicStrategy,
          8,
        ),
        paidStrategy: this.mergeUnique(
          existing.paidStrategy,
          plan.paidStrategy,
          8,
        ),
        paidTargeting: {
          coreDemographics: this.mergeUnique(
            existing.paidTargeting?.coreDemographics || [],
            plan.paidTargeting?.coreDemographics || [],
            8,
          ),
          interests: this.mergeUnique(
            existing.paidTargeting?.interests || [],
            plan.paidTargeting?.interests || [],
            8,
          ),
        },
        contentPillars: this.mergeUnique(
          existing.contentPillars,
          plan.contentPillars,
          8,
        ),
        cadence: existing.cadence || plan.cadence,
        kpis: this.mergeUnique(existing.kpis, plan.kpis, 8),
      });
    });

    const merged = Array.from(dedupedMap.values());
    const existing = new Set(merged.map((item) => item.platform));

    selectedPlatforms.forEach((platform) => {
      if (existing.has(platform)) return;
      merged.push(this.buildDefaultPlan(platform, app));
    });

    return merged;
  }

  private static buildFallbackPlatformPlans(
    platforms: SocialPlatform[],
    app: App,
  ): PlatformGrowthPlan[] {
    return platforms.map((platform) => this.buildDefaultPlan(platform, app));
  }

  private static buildDefaultPlan(
    platform: SocialPlatform,
    app: App,
  ): PlatformGrowthPlan {
    const profile = this.getPlatformProfile(platform, app);
    return {
      platform,
      whyItMatters: profile.whyItMatters,
      organicStrategy: profile.organicStrategy,
      paidStrategy: profile.paidStrategy,
      paidTargeting: {
        coreDemographics: profile.coreDemographics,
        interests: profile.interests,
      },
      contentPillars: profile.contentPillars,
      cadence: profile.cadence,
      kpis: profile.kpis,
    };
  }

  private static applyPlatformSpecificity(
    plans: PlatformGrowthPlan[],
    app: App,
  ): PlatformGrowthPlan[] {
    return plans.map((plan) => {
      const profile = this.getPlatformProfile(plan.platform, app);
      return {
        ...plan,
        whyItMatters:
          plan.whyItMatters && plan.whyItMatters.length > 20
            ? `${profile.whyItMatters} ${plan.whyItMatters}`.trim()
            : profile.whyItMatters,
        organicStrategy: this.mergeUnique(
          profile.organicStrategy,
          plan.organicStrategy,
          6,
        ),
        paidStrategy: this.mergeUnique(
          profile.paidStrategy,
          plan.paidStrategy,
          6,
        ),
        paidTargeting: {
          coreDemographics: this.mergeUnique(
            profile.coreDemographics,
            plan.paidTargeting?.coreDemographics || [],
            6,
          ),
          interests: this.mergeUnique(
            profile.interests,
            plan.paidTargeting?.interests || [],
            8,
          ),
        },
        contentPillars: this.mergeUnique(
          profile.contentPillars,
          plan.contentPillars,
          6,
        ),
        cadence: plan.cadence?.trim() || profile.cadence,
        kpis: this.mergeUnique(profile.kpis, plan.kpis, 6),
      };
    });
  }

  private static mergeUnique(
    primary: string[],
    secondary: string[],
    max: number,
  ): string[] {
    const merged = [...(primary || []), ...(secondary || [])]
      .map((item) => String(item || "").trim())
      .filter(Boolean);
    return [...new Set(merged)].slice(0, max);
  }

  private static getPlatformProfile(platform: SocialPlatform, app: App) {
    const category = app.category || "App";
    const appName = app.name;
    switch (platform) {
      case "tiktok":
        return {
          whyItMatters: `TikTok rewards high-retention short-form storytelling, which is ideal for showing fast ${category.toLowerCase()} outcomes in ${appName}.`,
          organicStrategy: [
            "Post 15-35s hook-first videos with a clear payoff in first 2 seconds",
            "Use trend-adjacent formats with native UGC style and comment replies",
            "Run creator seeding to produce authentic social proof clips",
          ],
          paidStrategy: [
            "Launch Spark Ads from strongest organic posts to preserve social proof",
            "Test 3 hooks x 2 CTAs weekly and cut creatives below retention targets",
            "Retarget video viewers with offer-focused conversion creatives",
          ],
          coreDemographics: [
            "18-34 mobile-first users",
            "Short-form video heavy users",
          ],
          interests: [
            `${category} tips`,
            "Lifestyle optimization",
            "Mobile app utility",
          ],
          contentPillars: [
            "Hook + Pain Point",
            "Before/After Outcome",
            "UGC Testimonials",
          ],
          cadence: "5-7 posts per week + 2 creator collaborations weekly",
          kpis: [
            "3-second hold rate",
            "CTR",
            "CPA",
            "Install-to-activation rate",
          ],
        };
      case "instagram":
        return {
          whyItMatters: `Instagram combines Reels discovery with visual trust-building, strong for ${category.toLowerCase()} apps that need brand + conversion.`,
          organicStrategy: [
            "Prioritize Reels with visual demos and concise outcome-focused captions",
            "Use carousel posts for feature education and save/share behavior",
            "Publish Stories with polls, FAQs, and objection handling",
          ],
          paidStrategy: [
            "Run Reels and Story placements with platform-native vertical creatives",
            "Use broad + lookalike + engaged audience ad sets with clear creative angles",
            "Retarget profile visitors and website/app engagers with conversion ads",
          ],
          coreDemographics: [
            "18-34 visual-first users",
            "Lifestyle and creator content consumers",
          ],
          interests: [
            `${category} content`,
            "Creator recommendations",
            "App productivity",
          ],
          contentPillars: [
            "Feature Demos",
            "Aspirational Lifestyle",
            "Community Proof",
          ],
          cadence: "4-6 Reels + daily Stories + 2 carousel posts weekly",
          kpis: ["Reel watch-through", "Profile-to-link CTR", "CPC", "ROAS"],
        };
      case "youtube":
        return {
          whyItMatters: `YouTube captures high-intent discovery and review behavior, which fits ${category.toLowerCase()} app education and trust-building.`,
          organicStrategy: [
            "Publish Shorts for rapid discovery and long-form explainers for conversion support",
            "Optimize titles/thumbnails around use-case and outcome keywords",
            "Create comparison/demo videos that reduce adoption friction",
          ],
          paidStrategy: [
            "Run Shorts ads for acquisition and in-feed/video action campaigns for installs",
            "Segment creatives by intent stage: awareness, consideration, conversion",
            "Retarget viewers of product demos with install-focused messaging",
          ],
          coreDemographics: [
            "18-44 intent-led users",
            "How-to and review content consumers",
          ],
          interests: [
            `${category} tutorials`,
            "Product reviews",
            "Self-improvement",
          ],
          contentPillars: [
            "How-To Education",
            "Use-Case Deep Dives",
            "Proof + Results",
          ],
          cadence: "3-5 Shorts + 1-2 long-form videos per week",
          kpis: [
            "View-through rate",
            "Video completion",
            "Cost per install",
            "Activation rate",
          ],
        };
      case "linkedin":
        return {
          whyItMatters: `LinkedIn is strongest for credibility-driven acquisition when ${category.toLowerCase()} positioning overlaps professional outcomes.`,
          organicStrategy: [
            "Post insight-led thought leadership tied to measurable outcomes",
            "Publish founder/operator narratives with practical frameworks",
            "Leverage employee advocacy and partner repost loops",
          ],
          paidStrategy: [
            "Use Lead Gen and Website/Traffic campaigns targeting role + industry",
            "Test single-image + document ads with strong proof points",
            "Retarget engaged audiences with demo/trial conversion messaging",
          ],
          coreDemographics: [
            "24-44 professionals",
            "Managers, founders, and operators",
          ],
          interests: [
            "Business growth",
            "Operational efficiency",
            `${category} tools`,
          ],
          contentPillars: [
            "Authority Insights",
            "Case Studies",
            "ROI Narratives",
          ],
          cadence: "3-4 posts weekly + 1 deep-dive case study weekly",
          kpis: [
            "Qualified CTR",
            "CPL/CPA",
            "Demo-to-install conversion",
            "Pipeline quality",
          ],
        };
      case "reddit":
        return {
          whyItMatters: `Reddit performs when value is proven in niche communities; ideal for ${category.toLowerCase()} use-cases with specific pain points.`,
          organicStrategy: [
            "Contribute non-promotional answers in relevant subreddits",
            "Share transparent build logs, feature updates, and user wins",
            "Use AMA-style threads to gather objections and improve positioning",
          ],
          paidStrategy: [
            "Target niche subreddit interests with direct, utility-first copy",
            "Test conversation ads and image ads with social proof headlines",
            "Retarget clickers with install-focused creatives",
          ],
          coreDemographics: [
            "18-39 research-oriented users",
            "Community-first decision makers",
          ],
          interests: [
            "Niche problem solving",
            `${category} communities`,
            "Tool discovery",
          ],
          contentPillars: [
            "Problem Solving",
            "Transparent Proof",
            "Community Feedback",
          ],
          cadence: "Daily engagement + 2-3 high-value posts weekly",
          kpis: [
            "Upvote/comment ratio",
            "CTR from community posts",
            "CPA",
            "Activation quality",
          ],
        };
      case "twitter":
        return {
          whyItMatters: `X/Twitter is effective for fast-moving discovery and opinion-driven reach, useful for rapid testing of ${category.toLowerCase()} messaging.`,
          organicStrategy: [
            "Ship concise threads with a strong first-tweet hook",
            "Use daily micro-content around wins, failures, and product updates",
            "Engage in category conversations and quote-reply strategically",
          ],
          paidStrategy: [
            "Run engagement and website click campaigns around high-performing hooks",
            "Test multiple opening lines and creative angles aggressively",
            "Retarget engaged users with install conversion ads",
          ],
          coreDemographics: [
            "18-44 news/opinion active users",
            "Early adopters and builders",
          ],
          interests: [
            "Tech and product trends",
            `${category} conversations`,
            "Growth tactics",
          ],
          contentPillars: [
            "Hot Takes + Insights",
            "Build-in-Public",
            "Feature Launches",
          ],
          cadence: "1-3 posts daily + 2 threads weekly",
          kpis: [
            "Engagement rate",
            "Link CTR",
            "Cost per install",
            "Share of voice",
          ],
        };
      case "facebook":
      default:
        return {
          whyItMatters: `Facebook still provides broad reach and efficient conversion inventory for ${category.toLowerCase()} audiences across age bands.`,
          organicStrategy: [
            "Post utility-focused videos and community posts in relevant groups",
            "Use testimonials and proof-driven posts with clear outcomes",
            "Leverage comments and DMs to reduce objections and drive intent",
          ],
          paidStrategy: [
            "Run Advantage+ and conversion campaigns with multiple creative angles",
            "Build lookalikes from high-quality activations, not just installs",
            "Retarget site/app engagers with conversion-optimized offers",
          ],
          coreDemographics: [
            "21-45 broad consumer segments",
            "Interest-based audiences",
          ],
          interests: [
            `${category} lifestyle`,
            "Mobile app users",
            "Problem-solution content",
          ],
          contentPillars: [
            "Outcome Stories",
            "Social Proof",
            "Educational Explainers",
          ],
          cadence: "4-5 posts weekly + active community moderation",
          kpis: ["CTR", "CPA", "ROAS", "Install-to-subscription conversion"],
        };
    }
  }

  private static buildFallbackGoToMarket(app: App): string[] {
    return [
      `Define core audience segments for ${app.name} and prioritize one beachhead segment first.`,
      "Ship a 4-week content and paid acquisition experiment plan with clear budget caps.",
      "Instrument activation and retention analytics before scaling spend.",
      "Scale only channels that hit target CAC and activation benchmarks.",
    ];
  }

  private static buildFallbackInsights(app: App): string[] {
    return [
      `${app.name} should emphasize a single, sharp value proposition in all channel messaging.`,
      "Short-form creative and proof-driven messaging typically improve top-of-funnel conversion.",
      "Early retention improvements generally have more growth impact than pure top-funnel expansion.",
    ];
  }

  private static buildActionChecklist(
    goToMarket: string[],
    insights: string[],
    plans: PlatformGrowthPlan[],
  ): MarketingActionChecklistItem[] {
    const seeds = [
      ...goToMarket.slice(0, 4),
      ...insights.slice(0, 2),
      ...plans
        .slice(0, 3)
        .map(
          (plan) =>
            `Launch ${this.readablePlatform(plan.platform)} paid test campaign`,
        ),
    ].filter(Boolean);

    return seeds.map((title, index) => ({
      id: `action_${index + 1}`,
      title,
      started: false,
    }));
  }

  private static buildFallbackBudgetChannelMixPlan(
    platforms: SocialPlatform[],
    budgetRange?: string,
  ): BudgetChannelMixPlan {
    const selected: SocialPlatform[] =
      platforms.length > 0 ? platforms : ["instagram", "tiktok", "youtube"];
    const recommendedMonthlyBudget = budgetRange || "$3,000-$10,000 / month";
    const weighted = selected.map((platform) => ({
      platform,
      weight: this.getPlatformBudgetWeight(platform),
    }));
    const totalWeight =
      weighted.reduce((sum, item) => sum + item.weight, 0) || 1;
    let remainder = 100;
    const withSplits = weighted.map((item, index) => {
      if (index === weighted.length - 1) {
        return {
          platform: item.platform,
          splitPercent: Math.max(1, remainder),
        };
      }
      const splitPercent = Math.max(
        1,
        Math.round((item.weight / totalWeight) * 100),
      );
      remainder -= splitPercent;
      return { platform: item.platform, splitPercent };
    });
    return {
      recommendedMonthlyBudget,
      spendSplit: withSplits.map(({ platform, splitPercent }) => ({
        platform,
        splitPercent,
        ...this.buildPlatformBudgetDetail(
          platform,
          splitPercent,
          recommendedMonthlyBudget,
          budgetRange,
        ),
      })),
    };
  }

  private static getPlatformBudgetWeight(platform: SocialPlatform): number {
    switch (platform) {
      case "tiktok":
        return 1.25;
      case "instagram":
        return 1.2;
      case "youtube":
        return 1.1;
      case "facebook":
        return 1.0;
      case "linkedin":
        return 0.9;
      case "twitter":
        return 0.85;
      case "reddit":
        return 0.75;
      default:
        return 1.0;
    }
  }

  private static buildPlatformBudgetDetail(
    platform: SocialPlatform,
    splitPercent: number,
    recommendedMonthlyBudget: string,
    budgetRange?: string,
  ): { testBudget: string; scaleCriteria: string } {
    const parsed = this.parseBudgetRangeToNumbers(
      budgetRange || recommendedMonthlyBudget,
    );
    const low = parsed?.min ?? 3000;
    const high = parsed?.max ?? 10000;
    const platformLow = Math.max(100, Math.round((low * splitPercent) / 100));
    const platformHigh = Math.max(
      platformLow,
      Math.round((high * splitPercent) / 100),
    );
    const testLow = Math.max(100, Math.round(platformLow * 0.3));
    const testHigh = Math.max(testLow, Math.round(platformHigh * 0.5));

    return {
      testBudget: `$${testLow.toLocaleString()}-$${testHigh.toLocaleString()} / month`,
      scaleCriteria: this.platformScaleCriteria(platform),
    };
  }

  private static platformScaleCriteria(platform: SocialPlatform): string {
    switch (platform) {
      case "tiktok":
        return "Scale when 3s hold rate and CTR beat baseline for 7 days while CPA remains under target.";
      case "instagram":
        return "Scale when Reel watch-through and profile/link CTR improve with stable CAC for 7 days.";
      case "youtube":
        return "Scale when view-through rate and cost-per-install improve across Shorts and in-feed placements.";
      case "linkedin":
        return "Scale when qualified lead/install rate rises and CPL/CPA stays within target for 2 weeks.";
      case "reddit":
        return "Scale when CTR and activation quality are strong in top-performing subreddit segments.";
      case "twitter":
        return "Scale when engagement-to-click conversion improves and install CPA stays below threshold.";
      case "facebook":
      default:
        return "Scale when CTR and conversion rate increase while CPA and ROAS meet scaling thresholds for 7 days.";
    }
  }

  private static parseBudgetRangeToNumbers(
    text?: string,
  ): { min: number; max: number } | null {
    if (!text) return null;
    const normalized = text.toLowerCase().replace(/,/g, "");
    const matches = normalized.match(/\$?\s*(\d+(?:\.\d+)?)\s*(k|m)?/g);
    if (!matches || matches.length === 0) return null;
    const values = matches
      .map((chunk) => {
        const parts = chunk.match(/(\d+(?:\.\d+)?)\s*(k|m)?/);
        if (!parts) return null;
        const n = Number(parts[1]);
        if (!Number.isFinite(n)) return null;
        const unit = parts[2];
        if (unit === "k") return Math.round(n * 1000);
        if (unit === "m") return Math.round(n * 1000000);
        return Math.round(n);
      })
      .filter((v): v is number => Number.isFinite(v));
    if (!values.length) return null;
    const min = Math.min(...values);
    const max = Math.max(...values);
    return { min, max: Math.max(min, max) };
  }

  private static determineCandidatePlatforms(app: App): SocialPlatform[] {
    const text = `${app.category || ""} ${app.description || ""}`.toLowerCase();
    const category = (app.category || "").toLowerCase();

    const set = new Set<SocialPlatform>();
    const add = (...platforms: SocialPlatform[]) =>
      platforms.forEach((p) => set.add(p));

    if (
      /b2b|enterprise|saas|workflow|crm|productivity|business|developer|dev tools|remote work/i.test(
        text,
      ) ||
      /business|productivity/i.test(category)
    ) {
      add("linkedin", "youtube", "twitter", "reddit");
    }

    if (
      /fitness|health|lifestyle|food|wellness|travel|shopping|dating|social|creator/i.test(
        text,
      )
    ) {
      add("instagram", "tiktok", "youtube", "facebook");
    }

    if (/gaming|game|esports/i.test(text)) {
      add("tiktok", "youtube", "reddit", "twitter");
    }

    if (/finance|crypto|trading/i.test(text)) {
      add("youtube", "twitter", "reddit", "facebook");
    }

    if (/education|learning|study|language/i.test(text)) {
      add("youtube", "tiktok", "instagram", "facebook");
    }

    if (set.size === 0) {
      add("instagram", "tiktok", "youtube");
    }

    return [...set];
  }

  private static defaultAssetTypeForPlatform(
    platform: SocialPlatform,
  ): "image" | "video" {
    if (
      platform === "tiktok" ||
      platform === "youtube" ||
      platform === "instagram"
    ) {
      return "video";
    }
    return "image";
  }

  private static defaultPromptForPlatform(
    platform: SocialPlatform,
    appName: string,
    assetType: "image" | "video",
  ): string {
    if (assetType === "video") {
      return `Create a ${this.readablePlatform(
        platform,
      )} vertical video ad concept for ${appName}: strong first-2-second hook, rapid scene transitions, social proof moment, explicit CTA end card, and a clear in-app outcome demonstration.`;
    }
    return `Create a high-converting ${this.readablePlatform(
      platform,
    )} static ad creative for ${appName}: bold focal point, problem-to-solution visual contrast, clean hierarchy, conversion-focused message space, native-feeling platform style, and a center-safe composition where core message and key visual elements stay within the middle 60% for easy cross-platform repurposing.`;
  }

  private static defaultPlatformTargetUsers(
    platform: SocialPlatform,
    app: App,
  ): string[] {
    const base = [
      `${app.category || "App"} interested users`,
      "Mobile-first users",
    ];
    if (platform === "linkedin") {
      return [
        "Professionals age 24-44",
        "Decision-makers and team leads",
        ...base,
      ];
    }
    if (platform === "tiktok" || platform === "instagram") {
      return ["Age 18-34", "Short-form content heavy users", ...base];
    }
    if (platform === "youtube") {
      return [
        "Intent-led learners and reviewers",
        "Long/short video consumers",
        ...base,
      ];
    }
    if (platform === "reddit") {
      return [
        "Niche community participants",
        "High-intent problem solvers",
        ...base,
      ];
    }
    return ["Age 18-44", ...base];
  }

  private static defaultCTAForPlatform(platform: SocialPlatform): string {
    if (platform === "linkedin") return "Learn More";
    if (platform === "reddit") return "Get Started";
    if (platform === "youtube") return "Install Now";
    return "Install Now";
  }

  private static buildDefaultAdCopyVariations(
    platform: SocialPlatform,
    appName: string,
  ) {
    const cta = this.defaultCTAForPlatform(platform);
    const tag = `#${appName.replace(/\s+/g, "")}`;
    return [
      {
        headline: `${appName} in Minutes`,
        primaryText: `Stop wasting time. ${appName} helps you get better results faster with a simple workflow.`,
        callToAction: cta,
        hashtags: [tag, "#mobileapp", "#productivity"],
      },
      {
        headline: `A Smarter Daily Routine`,
        primaryText: `Build momentum with ${appName}. One app, clear steps, measurable progress.`,
        callToAction: cta,
        hashtags: [tag, "#growth", "#app"],
      },
      {
        headline: `Make Better Decisions Faster`,
        primaryText: `${appName} gives you clarity and action in seconds, not hours.`,
        callToAction: cta,
        hashtags: [tag, "#results", "#digitaltools"],
      },
      {
        headline: `Your Shortcut to Better Outcomes`,
        primaryText: `Use ${appName} to simplify the process and stay consistent every day.`,
        callToAction: cta,
        hashtags: [tag, "#consistency", "#usergrowth"],
      },
    ];
  }

  private static normalizeVideoPromptVariations(
    value: unknown,
    platform: SocialPlatform,
  ): VideoPromptVariation[] {
    if (!Array.isArray(value))
      return this.buildDefaultVideoPromptVariations(platform, "Your App");
    const normalized = value
      .filter((item) => item && typeof item === "object")
      .map((item: any) => ({
        title: String(item.title || "").trim(),
        detailedPrompt: String(item.detailedPrompt || "").trim(),
        recommendedLengthSeconds: {
          geminiVeo: Number(item.recommendedLengthSeconds?.geminiVeo || 8),
          openAISora: Number(item.recommendedLengthSeconds?.openAISora || 8),
        },
      }))
      .filter((item) => item.title && item.detailedPrompt)
      .slice(0, 4);

    if (!normalized.length) {
      return this.buildDefaultVideoPromptVariations(platform, "Your App");
    }

    return normalized.map((item) => ({
      ...item,
      recommendedLengthSeconds: {
        geminiVeo: Math.max(
          4,
          Math.min(20, item.recommendedLengthSeconds.geminiVeo),
        ),
        openAISora: Math.max(
          4,
          Math.min(20, item.recommendedLengthSeconds.openAISora),
        ),
      },
    }));
  }

  private static buildDefaultVideoPromptVariations(
    platform: SocialPlatform,
    appName: string,
  ): VideoPromptVariation[] {
    const platformName = this.readablePlatform(platform);
    return [
      {
        title: `${platformName} Hook + Outcome`,
        detailedPrompt: `Create a vertical ad for ${appName}. 0-2s: pain-point hook. 2-6s: app workflow solving that pain with fast UI closeups. 6-8s: clear outcome and CTA moment. Bright lighting, clean framing, mobile-first pacing, conversion intent.`,
        recommendedLengthSeconds: { geminiVeo: 8, openAISora: 8 },
      },
      {
        title: `${platformName} Lifestyle Benefit Story`,
        detailedPrompt: `Generate a short-form video ad for ${appName}. Show real-life frustration, then a seamless shift to using the app, then a measurable positive result. Use energetic cuts, human-centered scenes, and a strong final action cue.`,
        recommendedLengthSeconds: { geminiVeo: 10, openAISora: 10 },
      },
      {
        title: `${platformName} Feature Demonstration`,
        detailedPrompt: `Produce a polished social video ad for ${appName} demonstrating top features in sequence: quick hook, feature 1, feature 2, feature 3, then final brand payoff. Maintain visual continuity and high readability of app interactions.`,
        recommendedLengthSeconds: { geminiVeo: 12, openAISora: 12 },
      },
      {
        title: `${platformName} UGC-Style Testimonial`,
        detailedPrompt: `Create a UGC-inspired ad video for ${appName}. Start with authentic reaction shot, then practical use flow, then results proof with emotional relief, ending with decisive CTA. Keep it native to social feeds and retention-optimized.`,
        recommendedLengthSeconds: { geminiVeo: 8, openAISora: 8 },
      },
    ];
  }

  private static resolveRecommendedVideoSeconds(
    variation: VideoPromptVariation,
    provider: "gemini" | "openai",
  ): number {
    if (provider === "openai") {
      return Math.max(
        4,
        Math.min(
          20,
          Number(variation.recommendedLengthSeconds.openAISora || 8),
        ),
      );
    }
    return Math.max(
      4,
      Math.min(20, Number(variation.recommendedLengthSeconds.geminiVeo || 8)),
    );
  }

  private static mapReport(data: any): MarketingResearchReport {
    const inferredApp = {
      appId: data.appId,
      name: data.appName,
      description: "",
      category: data.appCategory || "General",
      platform: "both",
      storeUrl: "",
      userId: data.userId,
      createdAt: new Date(),
      updatedAt: new Date(),
    } as App;

    const selectedPlatforms = this.determineCandidatePlatforms(inferredApp);
    const platformPlans = this.normalizePlatformPlans(data.platformPlans);
    const mergedPlans =
      platformPlans.length > 0
        ? this.mergePlatformPlans(platformPlans, selectedPlatforms, inferredApp)
        : this.buildFallbackPlatformPlans(selectedPlatforms, inferredApp);
    const goToMarket = this.normalizeStringArray(data.goToMarketStrategy, 10);
    const marketingInsights = this.normalizeStringArray(
      data.marketingInsights,
      20,
    );
    const resolvedGoToMarket =
      goToMarket.length > 0
        ? goToMarket
        : this.buildFallbackGoToMarket(inferredApp);
    const resolvedInsights =
      marketingInsights.length > 0
        ? marketingInsights
        : this.buildFallbackInsights(inferredApp);

    return {
      reportId: data.reportId,
      userId: data.userId,
      appId: data.appId,
      appName: data.appName,
      appCategory: data.appCategory,
      model: data.model,
      summary: data.summary,
      coreTargetAudience: this.normalizeCoreTargetAudience(
        data.coreTargetAudience,
        inferredApp,
      ),
      budgetChannelMixPlan: this.normalizeBudgetChannelMixPlan(
        data.budgetChannelMixPlan,
        selectedPlatforms,
        data.requestedBudgetRange,
      ),
      goToMarketStrategy: resolvedGoToMarket,
      marketingInsights: resolvedInsights,
      platformPlans: mergedPlans,
      adRecommendations: this.ensureRecommendationCoverage(
        this.normalizeAdRecommendations(data.adRecommendations),
        mergedPlans,
        selectedPlatforms,
        inferredApp,
      ),
      actionChecklist:
        Array.isArray(data.actionChecklist) && data.actionChecklist.length > 0
          ? data.actionChecklist
              .map((item: any, index: number) => ({
                id: String(item?.id || `action_${index + 1}`),
                title: String(item?.title || "").trim(),
                started: Boolean(item?.started),
              }))
              .filter(
                (item: MarketingActionChecklistItem) => item.title.length > 0,
              )
          : this.buildActionChecklist(
              resolvedGoToMarket,
              resolvedInsights,
              mergedPlans,
            ),
      requestedBudgetRange:
        data.requestedBudgetRange &&
        String(data.requestedBudgetRange).trim().length > 0
          ? String(data.requestedBudgetRange).trim()
          : undefined,
      createdAt: this.toDate(data.createdAt),
      savedAt: data.savedAt ? this.toDate(data.savedAt) : undefined,
    };
  }

  private static dedupeReports(
    reports: MarketingResearchReport[],
  ): MarketingResearchReport[] {
    const byId = new Map<string, MarketingResearchReport>();
    reports.forEach((report) => {
      const existing = byId.get(report.reportId);
      if (!existing) {
        byId.set(report.reportId, report);
        return;
      }
      const existingTime = (existing.savedAt || existing.createdAt).getTime();
      const nextTime = (report.savedAt || report.createdAt).getTime();
      if (nextTime >= existingTime) {
        byId.set(report.reportId, report);
      }
    });

    return Array.from(byId.values()).sort(
      (a, b) =>
        (b.savedAt || b.createdAt).getTime() -
        (a.savedAt || a.createdAt).getTime(),
    );
  }

  private static readablePlatform(platform: SocialPlatform): string {
    if (platform === "twitter") return "X / Twitter";
    return platform.charAt(0).toUpperCase() + platform.slice(1);
  }

  private static mapAsset(data: any): MarketingAsset {
    const mediaType: "image" | "video" =
      data.mediaType === "video" || data.type === "video" ? "video" : "image";
    return {
      assetRecordId: data.assetRecordId,
      userId: data.userId,
      appId: data.appId,
      appName: data.appName,
      reportId: data.reportId,
      recommendationId: data.recommendationId,
      platform: this.isValidPlatform(data.platform)
        ? data.platform
        : "instagram",
      mediaType,
      prompt: data.prompt || "",
      headline: data.headline || "",
      primaryText: data.primaryText || "",
      callToAction: data.callToAction || "",
      hashtags: this.normalizeStringArray(data.hashtags, 8),
      image:
        mediaType === "image"
          ? {
              assetId: data.image?.assetId || "",
              provider: data.image?.provider || "gemini",
              model: data.image?.model || "",
              downloadUrl: data.image?.downloadUrl || "",
              storagePath: data.image?.storagePath || "",
              mimeType: data.image?.mimeType || "image/png",
              sizeBytes: Number(data.image?.sizeBytes || 0),
              createdAt: data.image?.createdAt || "",
            }
          : undefined,
      video:
        mediaType === "video"
          ? {
              assetId: data.video?.assetId || "",
              provider: data.video?.provider || "gemini",
              model: data.video?.model || "",
              downloadUrl: data.video?.downloadUrl || "",
              storagePath: data.video?.storagePath || "",
              mimeType: data.video?.mimeType || "video/mp4",
              sizeBytes: Number(data.video?.sizeBytes || 0),
              createdAt: data.video?.createdAt || "",
            }
          : undefined,
      createdAt: this.toDate(data.createdAt),
    };
  }

  private static mapCreator(data: any): MarketingCreator {
    const validStatus: MarketingCreator["status"][] = [
      "planned",
      "contacted",
      "contracted",
      "completed",
      "cancelled",
    ];
    const status = validStatus.includes(data.status) ? data.status : "planned";
    return {
      creatorId: data.creatorId || "",
      userId: data.userId || "",
      appId: data.appId || "",
      reportId: data.reportId || undefined,
      name: data.name || "",
      handle: data.handle || undefined,
      platform: data.platform || "Unknown",
      audienceSize:
        Number.isFinite(Number(data.audienceSize)) &&
        Number(data.audienceSize) > 0
          ? Number(data.audienceSize)
          : undefined,
      niche: data.niche || undefined,
      email: data.email || undefined,
      ratePaid: Math.max(0, Number(data.ratePaid || 0)),
      currency: data.currency || "USD",
      status,
      notes: data.notes || undefined,
      createdAt: this.toDate(data.createdAt),
      updatedAt: data.updatedAt ? this.toDate(data.updatedAt) : undefined,
    };
  }

  private static toDate(value: any): Date {
    if (value instanceof Timestamp) return value.toDate();
    if (value?.toDate && typeof value.toDate === "function")
      return value.toDate();
    return new Date(value || Date.now());
  }

  private static generateId(prefix: string): string {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }

  private static parseJsonResponse<T>(text: string): T | null {
    const source = String(text || "").trim();
    if (!source) return null;

    try {
      return JSON.parse(source) as T;
    } catch {
      // Continue to fenced and brace extraction parsing.
    }

    const fencedMatch = source.match(/```json\s*([\s\S]*?)```/i);
    if (fencedMatch?.[1]) {
      try {
        return JSON.parse(fencedMatch[1].trim()) as T;
      } catch {
        // Continue to brace extraction parsing.
      }
    }

    const firstBrace = source.indexOf("{");
    const lastBrace = source.lastIndexOf("}");
    if (firstBrace >= 0 && lastBrace > firstBrace) {
      const candidate = source.slice(firstBrace, lastBrace + 1);
      try {
        return JSON.parse(candidate) as T;
      } catch {
        return null;
      }
    }

    return null;
  }
}
